import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { useIntercom } from 'react-use-intercom';

import { useAuth } from './contexts/AuthContext';
import { usePatientAuth } from './contexts/PatientAuthContext';

import AppRoutes from './AppRoutes';
import GtmHelpers from './lib/gtm';
import Auth0Helpers from './lib/auth0';
import Config from './lib/config';
import { formatEnvironmentDocumentTitle } from './lib/environment';
import { Environment } from './v2/types/config';
import { useProviderColors } from './v2/hooks/useProviderColors';
import { useIntercomJwtRefresh } from './v2/hooks/useIntercomJwtRefresh';

GtmHelpers.initGtm();
Auth0Helpers.initAuth0();

const App: React.FC = () => {
  const { tokenPayload, authedProviderUser, refreshAuthedProviderUser } =
    useAuth();
  const { authedPatient } = usePatientAuth();

  useProviderColors({
    providerPrimaryColor:
      authedProviderUser?.primaryColor ?? authedPatient?.providerPrimaryColor,
    providerSecondaryColor:
      authedProviderUser?.secondaryColor ??
      authedPatient?.providerSecondaryColor,
  });

  const {
    boot: bootIntercom,
    shutdown: shutdownIntercom,
    update: updateIntercom,
  } = useIntercom();

  useEffect(() => {
    if (Config.REACT_APP_ENVIRONMENT === Environment.Development) {
      return;
    }

    if (
      (tokenPayload && !tokenPayload.isV2) ||
      (authedProviderUser && !authedProviderUser.isV2) ||
      Boolean(authedPatient?.programInstanceId)
    ) {
      // Legacy V1 provider account or patient authed in Client Portal
      shutdownIntercom();
    } else {
      // Unauthed or provider authed in V2
      bootIntercom();
      if (authedProviderUser?.isV2) {
        updateIntercom({
          userId: authedProviderUser.id,
          email: authedProviderUser.email,
          name: authedProviderUser.name,
          // Proves this identity to Intercom rather than asserting it. Null while the server has no
          // signing secret configured, which leaves the Messenger booting unverified as before —
          // but once Messenger Security is enforced, an absent token stops it loading entirely.
          // `?? undefined` is deliberate: react-use-intercom strips undefined keys before calling
          // Intercom but forwards null, and we want the field omitted rather than sent as null.
          intercomUserJwt: authedProviderUser.intercomUserJwt ?? undefined,
        });
      }
    }
  }, [tokenPayload, authedProviderUser, authedPatient]);

  useIntercomJwtRefresh({
    intercomUserJwt: authedProviderUser?.intercomUserJwt,
    refreshAuthedProviderUser,
  });

  return (
    <>
      <Helmet>
        <title>{formatEnvironmentDocumentTitle()}</title>
      </Helmet>
      <Toaster position="bottom-center" />
      <AppRoutes />
    </>
  );
};

export default App;
